import {
  Document,
  Font,
  Image,
  Page,
  StyleSheet,
  Text,
  View,
} from "@react-pdf/renderer";
import { PayslipDetailed } from "../payslipAPI";
import { useTranslation } from "react-i18next";
import formatCurrency from "../../../../common/helpers/formatCurrency";
import formatNumber from "../../../../common/helpers/formatNumber";
import { format } from "date-fns";
import { leadingZerosDateFormat } from "../../../../common/helpers/formatDateTime";
import { HEALTH_INSURANCE_PER_DIEM_RATE } from "../../constants";

type Props = {
  payslip: PayslipDetailed | undefined;
  localizedExpenseTypes: { [key: string]: string };
};

Font.register({
  family: "Quicksand",
  src: "/fonts/Quicksand-Bold.ttf",
  fontStyle: "normal",
  fontWeight: "bold",
});
Font.register({
  family: "Source Sans Pro",
  fonts: [
    { src: "/fonts/SourceSans3-Regular.ttf" },
    {
      src: "/fonts/SourceSans3-SemiBold.ttf",
      fontStyle: "normal",
      fontWeight: 600,
    },
    {
      src: "/fonts/SourceSans3-Bold.ttf",
      fontStyle: "normal",
      fontWeight: 700,
    },
  ],
});

const styles = StyleSheet.create({
  document: {
    fontSize: 12,
    fontFamily: "Source Sans Pro",
  },
  section: {
    padding: 10,
    margin: "10px 30px 0px 30px",
    borderBottom: "1px solid #F1EDF7",
  },
  grid: {
    display: "flex",
    flexDirection: "row",
  },
  row: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 5,
  },
  heading1: {
    fontSize: 16,
    fontWeight: 700,
    marginBottom: 10,
    fontFamily: "Quicksand",
  },
  subheading: {
    fontWeight: 600,
  },
  salaryHeading: {
    fontFamily: "Quicksand",
    fontWeight: 600,
    backgroundColor: "#F1EDF7",
    padding: "5px 10px",
    marginBottom: 5,
    width: "100%",
  },
  divider: {
    borderBottom: "1px solid #F1EDF7",
    marginLeft: -10,
    marginRight: -10,
    marginTop: 10,
    marginBottom: 15,
  },
  img: {
    width: 34,
    height: 34,
  },
});

export const PayslipPDF = ({ payslip, localizedExpenseTypes }: Props) => {
  const { t } = useTranslation();

  const payslipHealthInsuranceRate = Number(
    payslip?.employeeHealthInsuranceRate || HEALTH_INSURANCE_PER_DIEM_RATE,
  ).toLocaleString("fi");

  return payslip ? (
    <Document style={styles.document}>
      <Page size="A4">
        <View style={styles.section}>
          <View style={[styles.row, { marginBottom: 10 }]}>
            <Text style={{ fontFamily: "Quicksand" }}>
              {t("payslips:payslip").toLocaleUpperCase()}
            </Text>
          </View>
          <View style={styles.grid}>
            {/* grid left */}
            <View style={{ marginRight: 20 }}>
              <Image src="/assets/eezy_logo.png" style={styles.img} />
            </View>
            {/* grid right */}
            <View>
              <Text
                style={styles.heading1}
              >{`${t("schedulingPeriods:schedulingPeriod")} ${format(
                new Date(payslip.periodStart),
                "dd.MM.yyyy",
              )} - ${format(new Date(payslip.periodEnd), "dd.MM.yyyy")}`}</Text>
              <Text>{`${t("payslips:receiptNumber")} ${payslip.payslipNumber}`}</Text>
            </View>
          </View>
        </View>
        <View style={[styles.section, { fontSize: 14 }]}>
          <View style={styles.row}>
            <Text>{t("payslips:salaryDate")}</Text>
            <Text>{format(payslip.salaryDate, leadingZerosDateFormat)}</Text>
          </View>
          <View style={styles.row}>
            <Text>{t("payslips:payer")}</Text>
            <Text>{payslip.payer.name}</Text>
          </View>
        </View>
        <View style={[styles.section, { fontSize: 14 }]}>
          <View style={styles.row}>
            <Text>{t("payslips:recipient")}</Text>
            <Text>{payslip.recipient.name}</Text>
          </View>
          <View style={styles.row}>
            <Text>{t("payslips:ssn")}</Text>
            <Text>{payslip.recipient.ssn}</Text>
          </View>
          <View style={styles.row}>
            <Text>{t("payslips:bank")}</Text>
            <Text>{payslip.recipient.iban}</Text>
          </View>
        </View>
        <View style={styles.section}>
          <Text style={styles.heading1}>
            {t("payslips:taxDeductionCardAndInfo")}
          </Text>
          <Text style={{ fontSize: 14, marginBottom: 15 }}>
            {t("payslips:accumulationFromAllEezyCompanies")}
          </Text>
          <View style={styles.row}>
            <Text>{t("taxDeductionCard:basicWithholdingRate")}</Text>
            <Text>{formatNumber(payslip.recipient.withholdingRate)}</Text>
          </View>
          <View style={styles.row}>
            <Text>{t("taxDeductionCard:additionalWithholdingRate")}</Text>
            <Text>
              {formatNumber(payslip.recipient.additionalWithholdingRate)}
            </Text>
          </View>
          <View style={styles.row}>
            <Text>{t("taxDeductionCard:incomeCeilingAnnual")}</Text>
            <Text>{formatCurrency(payslip.recipient.incomeCeiling)}</Text>
          </View>
          <View style={styles.row}>
            <Text>{t("taxDeductionCard:incomeFromTaxPeriodBeginning")}</Text>
            <Text>{formatCurrency(payslip.recipient.taxable)}</Text>
          </View>
          <View style={styles.row}>
            <Text>
              {t("taxDeductionCard:withholdingFromTaxPeriodBeginning")}
            </Text>
            <Text>{formatCurrency(payslip.recipient.taxWithholding)}</Text>
          </View>
        </View>
        <View style={styles.section}>
          <Text style={styles.heading1}>{t("talentProfile:salary")}</Text>
          {payslip.rows.map((row, i) => (
            <>
              {row.title ? (
                <View style={styles.row} wrap={false}>
                  <Text style={styles.salaryHeading}>
                    {row.title.toLocaleUpperCase()}
                  </Text>
                </View>
              ) : (
                <Text style={styles.divider} />
              )}

              {row.rows.map((item, i) => (
                <>
                  <View style={styles.row} key={i}>
                    <Text
                      style={styles.subheading}
                    >{`${localizedExpenseTypes[item.expenseTypeCode]} `}</Text>
                  </View>
                  <View style={styles.row}>
                    <Text>{item.description}</Text>

                    <Text>{formatCurrency(item.expense)}</Text>
                  </View>
                </>
              ))}
            </>
          ))}
        </View>
        <View style={styles.section}>
          <View style={styles.row}>
            <Text>{t("payslips:taxWithholding")}</Text>
            <Text>
              {formatCurrency(payslip.subtotalsPeriod.taxWithholding)}
            </Text>
          </View>
          <View style={styles.row}>
            <Text>{t("payslips:pensionContribution")}</Text>
            <Text>
              {formatCurrency(payslip.subtotalsPeriod.pensionInsurance)}
            </Text>
          </View>
          <View style={styles.row}>
            <Text>{t("payslips:unemploymentInsuranceLong")}</Text>
            <Text>
              {formatCurrency(payslip.subtotalsPeriod.unemploymentInsurance)}
            </Text>
          </View>
          <View style={styles.row}>
            <Text>{`${t(
              "payslips:sickBenefit",
            )} (${payslipHealthInsuranceRate} %)`}</Text>
            <Text>
              {formatCurrency(payslip.subtotalsPeriod.healthInsurancePerDiem)}
            </Text>
          </View>
          <View style={styles.row}>
            <Text>{t("payslips:taxExemptLong")}</Text>
            <Text>{formatCurrency(payslip.subtotalsPeriod.taxExempt)}</Text>
          </View>
        </View>
        <View style={styles.section}>
          <View style={styles.row}>
            <Text>{t("payslips:grossSalary")}</Text>
            <Text>{formatCurrency(payslip.subtotalsPeriod.incomeGross)}</Text>
          </View>
          <View style={[styles.row, styles.subheading, { fontSize: 14 }]}>
            <Text>{t("talentProfile:totalPayment")}</Text>
            <Text>{formatCurrency(payslip.subtotalsPeriod.incomeNet)}</Text>
          </View>
        </View>
        <View style={styles.section}>
          <Text style={styles.heading1}>{t("payslips:salaryCertificate")}</Text>
          <View style={styles.row}>
            <View>
              <Text style={styles.subheading}>{t("payslips:grossSalary")}</Text>
              <Text>{t("payslips:incomeGross")}</Text>
              <Text>{t("payslips:fringeBenefitsLong")}</Text>
              <Text>{t("payslips:taxableLong")}</Text>
              <Text>{t("payslips:taxExempt")}</Text>
              <Text>{t("payslips:taxWithholding")}</Text>
            </View>
            <View>
              <Text style={styles.subheading}>{t("payslips:period")}</Text>
              <Text>{formatCurrency(payslip.subtotalsPeriod.incomeGross)}</Text>
              <Text>
                {formatCurrency(payslip.subtotalsPeriod.fringeBenefits)}
              </Text>
              <Text>{formatCurrency(payslip.subtotalsPeriod.taxable)}</Text>
              <Text>{formatCurrency(payslip.subtotalsPeriod.taxExempt)}</Text>
              <Text>
                {formatCurrency(payslip.subtotalsPeriod.taxWithholding)}
              </Text>
            </View>
            <View>
              <Text style={styles.subheading}>{t("payslips:presentYear")}</Text>
              <Text>{formatCurrency(payslip.subtotalsYear.incomeGross)}</Text>
              <Text>
                {formatCurrency(payslip.subtotalsYear.fringeBenefits)}
              </Text>
              <Text>{formatCurrency(payslip.subtotalsYear.taxable)}</Text>
              <Text>{formatCurrency(payslip.subtotalsYear.taxExempt)}</Text>
              <Text>
                {formatCurrency(payslip.subtotalsYear.taxWithholding)}
              </Text>
            </View>
          </View>
        </View>
      </Page>
    </Document>
  ) : null;
};
